



















































































































































































































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import { useCreateBank, useUpdateBank } from '@/composable/bank';
import { AxiosError } from 'axios';

import { cloneDeep } from 'lodash';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import { IBank, IBankData, Bank } from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';
import { requiredValidator } from '@/utils/validators';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import PhoneInputPicker from '@/components/core/PhoneInputPicker/Index.vue';
import CountryPicker from '@/components/common/CountryPicker.vue';

export default defineComponent({
  name: 'BankDialog',
  components: { ALDialog, ErrorAlert, PhoneInputPicker, CountryPicker },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    bank: {
      type: Object as PropType<IBankData | null>,
      default: null
    }
  },
  emits: ['update:dialog'],
  setup(props, { emit }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const bankData = ref<IBankData>({ address: {} });

    watch(localDialog, value => {
      if (value) {
        resetFormValidation();
        clearErrors();
        bankData.value = !props.isEdit
          ? ({ address: {} } as IBankData)
          : cloneDeep(props.bank!);
      }
    });

    const bankFormRef = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () => `${props.isEdit ? 'Update' : 'Create'} bank information`
    );

    function closeDialog() {
      resetFormValidation();
      clearErrors();
      localDialog.value = false;
    }

    const {
      isLoading: isUpdatePending,
      error: updateError,
      mutate: updateBank,
      reset: updateReset
    } = useUpdateBank();
    const {
      isLoading: isCreatePending,
      error: createError,
      mutate: createBank,
      reset: createReset
    } = useCreateBank();

    const localError = ref<ISystemError | null>(null);

    const loading = computed(() =>
      props.isEdit ? isUpdatePending.value : isCreatePending.value
    );

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = props.isEdit ? updateError.value : createError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    function onPhoneNumberInput(
      _formattedNumber: string,
      { number }: { number: { international: string } }
    ) {
      bankData.value.phone1 = number.international;
      clearErrors();
    }

    function onPhoneNumber2Input(
      _formattedNumber: string,
      { number }: { number: { international: string } }
    ) {
      bankData.value.phone2 = number.international;
      clearErrors();
    }

    function clearErrors() {
      props.isEdit ? updateReset() : createReset();
      error.value = null;
    }

    function resetFormValidation(): void {
      if (bankFormRef.value) {
        return bankFormRef.value.resetValidation();
      }
    }

    function validateForm(): boolean {
      let valid = false;
      if (bankFormRef.value) {
        valid = bankFormRef.value.validate();
      }
      return valid;
    }

    async function save() {
      clearErrors();
      const valid = await validateForm();

      if (valid) {
        let bankInstance: IBank | null = null;

        try {
          bankInstance = new Bank(bankData.value);
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (bankInstance != null) {
          const saveAction = props.isEdit ? updateBank : createBank;

          let payload = bankInstance;
          if (props.isEdit) {
            payload = diffDeep(bankInstance, props.bank, true);
          }

          saveAction(payload, {
            onSuccess: () => {
              closeDialog();
            }
          });
        }
      }
    }

    return {
      localDialog,
      bankData,
      valid,
      bankFormRef,
      subtitle,
      requiredValidator,
      closeDialog,
      save,
      clearErrors,
      onPhoneNumberInput,
      onPhoneNumber2Input,
      loading,
      error
    };
  }
});
